import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Modal, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../common/FalconCardHeader';
import AdvanceTable from '../common/advance-table/AdvanceTable';
import AdvanceTablePagination from '../common/advance-table/AdvanceTablePagination';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { createColumnHelper } from '@tanstack/react-table';
import Avatar from '../common/Avatar';
import { toast } from 'react-toastify';
import { userService, roleService, businessAreaService } from '../../services/api';
import { isSuperAdmin } from '../../utils/auth';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [businessAreas, setBusinessAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    roles: [],
    businessAreas: [],
    isActive: true,
    password: '',
    confirmPassword: ''
  });

  // Usar useRef para controlar la carga inicial y evitar duplicados
  const hasLoadedData = useRef(false);
  const loadingPromise = useRef(null);
  const refreshInterval = useRef(null);

  const statuses = [
    { value: true, label: 'Activo' },
    { value: false, label: 'Inactivo' }
  ];

  // Cargar datos iniciales - optimizado para React Strict Mode
  useEffect(() => {
    // Si ya se cargaron los datos o hay una carga en progreso, salir
    if (hasLoadedData.current || loadingPromise.current) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const [usersData, rolesData, businessAreasData] = await Promise.all([
          userService.getAll(),
          roleService.getAll(),
          businessAreaService.getAll()
        ]);
        
        setUsers(usersData);
        setRoles(rolesData);
        setBusinessAreas(businessAreasData);
        
        // Marcar como completado
        hasLoadedData.current = true;
      } catch (error) {
        toast.error('Error al cargar los datos');
        // No marcar como completado para permitir reintentos
      } finally {
        setLoading(false);
        loadingPromise.current = null;
      }
    };

    // Crear la promesa de carga
    loadingPromise.current = loadData();

    // Cleanup function para React Strict Mode
    return () => {
      if (loadingPromise.current) {
        loadingPromise.current = null;
      }
    };
  }, []);

  // Función para actualizar solo la lista de usuarios
  const refreshUsers = useCallback(async () => {
    try {
      const usersData = await userService.getAll();
      setUsers(usersData);
    } catch (error) {
      toast.error('Error al refrescar los usuarios');
    }
  }, []);

  // Efecto para actualizar datos de usuarios cada 30 segundos (para mostrar estado en línea)
  useEffect(() => {
    refreshInterval.current = setInterval(() => {
      refreshUsers();
    }, 30000); // 30 segundos

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [refreshUsers]);

  const columnHelper = createColumnHelper();


  const columns = useMemo(
    () => [
      columnHelper.accessor('avatar', {
        header: '',
        cell: ({ row }) => (
          <Avatar
            size="2xl"
            name={`${row.original.fullName}`}
            src={row.original.avatar}
          />
        ),
        meta: { headerProps: { style: { width: '60px' } } }
      }),
      columnHelper.accessor('fullName', {
        header: 'Usuario',
        cell: ({ row }) => (
          <div>
            <h6 className="mb-0 fw-semibold">
              {row.original.fullName}
            </h6>
            <small className="text-muted">{row.original.email}</small>
            <br />
            <small className="text-muted">@{row.original.username}</small>
          </div>
        )
      }),
      columnHelper.accessor('roles', {
        header: 'Roles',
        cell: ({ getValue }) => {
          const userRoles = getValue() || [];
          return (
            <div>
              {userRoles.map((role, index) => {
                const variant = role.name === 'admin' ? 'danger' : 
                              role.name === 'manager' ? 'info' : 
                              role.name === 'user' ? 'warning' : 'secondary';
                return (
                  <Badge key={index} bg={variant} className="fs-11 me-1">
                    <FontAwesomeIcon icon="user" className="me-1" />
                    {role.name}
                  </Badge>
                );
              })}
            </div>
          );
        }
      }),
      columnHelper.accessor('businessAreas', {
        header: 'Áreas de Negocio',
        cell: ({ getValue }) => {
          const areas = getValue() || [];
          return (
            <div>
              {areas.length > 0 ? (
                areas.map((area, index) => (
                  <small key={index} className="text-muted d-block">
                    {area.name}
                  </small>
                ))
              ) : (
                <small className="text-muted">Sin áreas asignadas</small>
              )}
            </div>
          );
        }
      }),
      columnHelper.accessor('isActive', {
        header: 'Estado',
        cell: ({ getValue }) => {
          const isActive = getValue();
          return (
            <Badge bg={isActive ? 'success' : 'secondary'} className="fs-11">
              <FontAwesomeIcon 
                icon={isActive ? 'check-circle' : 'circle'} 
                className="me-1" 
              />
              {isActive ? 'Activo' : 'Inactivo'}
            </Badge>
          );
        }
      }),
      columnHelper.accessor('createdAt', {
        header: 'Creado',
        cell: ({ getValue }) => {
          const date = new Date(getValue());
          return (
            <small className="text-muted">
              {date.toLocaleDateString('es-ES')}
            </small>
          );
        }
      }),
      
      columnHelper.accessor('lastActivity', {
        header: 'Última Sesión',
        cell: ({ row }) => {
          const user = row.original;
          const lastLogin = user.lastLoginAt;
          const lastLogout = user.lastLogoutAt;
          const isOnline = user.isOnline;
          
          if (!lastLogin) {
            return (
              <div className="text-center">
                <Badge bg="secondary" className="fs-11 mb-1">
                  <FontAwesomeIcon icon="clock" className="me-1" />
                  Sin actividad
                </Badge>
              </div>
            );
          }
          
          // Mostrar la fecha más reciente entre login y logout
          const lastActivity = lastLogout && new Date(lastLogout) > new Date(lastLogin) 
            ? new Date(lastLogout) 
            : new Date(lastLogin);
          
          const now = new Date();
          const diffInMinutes = Math.floor((now - lastActivity) / (1000 * 60));
          const diffInHours = Math.floor(diffInMinutes / 60);
          const diffInDays = Math.floor(diffInHours / 24);
          
          let timeAgo;
          if (isOnline) {
            timeAgo = 'Conectado ahora';
          } else if (diffInMinutes < 1) {
            timeAgo = 'Hace menos de 1 min';
          } else if (diffInMinutes < 60) {
            timeAgo = `Hace ${diffInMinutes} min`;
          } else if (diffInHours < 24) {
            timeAgo = `Hace ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
          } else {
            timeAgo = `Hace ${diffInDays} día${diffInDays > 1 ? 's' : ''}`;
          }
          
          return (
            <div className="text-center">
              <div className="mb-1">
                <Badge bg={isOnline ? 'success' : diffInHours < 1 ? 'warning' : diffInHours < 24 ? 'info' : 'secondary'} className="fs-11">
                  <FontAwesomeIcon 
                    icon={isOnline ? 'circle' : 'clock'} 
                    className="me-1" 
                  />
                  {timeAgo}
                </Badge>
              </div>
              <div className="text-muted" style={{fontSize: '11px', lineHeight: '1.2'}}>
                <div>{lastActivity.toLocaleDateString('es-ES', { 
                  day: '2-digit', 
                  month: '2-digit',
                  year: '2-digit'
                })}</div>
                <div>{lastActivity.toLocaleTimeString('es-ES', { 
                  hour: '2-digit', 
                  minute: '2-digit'
                })}</div>
              </div>
            </div>
          );
        }
      }),
      columnHelper.accessor('actions', {
        header: 'Acciones',
        cell: ({ row }) => (
          <div className="text-center">
            <Button
              variant="falcon-default"
              size="sm"
              className="me-1"
              onClick={() => handleEdit(row.original)}
              title="Editar usuario"
            >
              <FontAwesomeIcon icon="edit" />
            </Button>
            <Button
              variant="falcon-default"
              size="sm"
              className="text-danger"
              onClick={() => handleDelete(row.original)}
              title="Eliminar usuario"
              disabled={row.original.roles?.some(role => role.name === 'admin')}
            >
              <FontAwesomeIcon icon="trash" />
            </Button>
          </div>
        ),
        meta: { headerProps: { className: 'text-center' } }
      })
    ],
    [columnHelper]
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      setSaving(true);
      
      const userData = {
        username: formData.username,
        email: formData.email,
        fullName: formData.fullName,
        password: formData.password,
        roles: formData.roles,
        businessAreas: formData.businessAreas
      };

      // Solo incluir isActive en edición, no en creación
      if (editingUser) {
        userData.isActive = formData.isActive;
        // Si no hay nueva contraseña, no enviarla
        if (!userData.password) {
          delete userData.password;
        }
        await userService.update(editingUser.id, userData);
        toast.success('Usuario actualizado exitosamente');
      } else {
        // Para nuevos usuarios, no incluir isActive (el backend lo establece por defecto en true)
        await userService.create(userData);
        toast.success('Usuario creado exitosamente');
      }

      await refreshUsers(); // Recargar datos
      resetForm();
      setShowUserModal(false);
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      
      // Extraer mensaje de error más específico
      let errorMessage = 'Error al guardar el usuario';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Si hay un array de mensajes de validación
        if (errorData.error?.message && Array.isArray(errorData.error.message)) {
          errorMessage = errorData.error.message.join(', ');
        }
        // Si hay un mensaje de error directo
        else if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        }
        // Si hay un mensaje en el nivel superior
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      email: user.email,
      fullName: user.fullName,
      roles: user.roles?.map(role => role.id) || [],
      businessAreas: user.businessAreas?.map(area => area.id) || [],
      isActive: user.isActive,
      password: '',
      confirmPassword: ''
    });
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await userService.delete(userToDelete.id);
      toast.success('Usuario eliminado exitosamente');
      await refreshUsers(); // Recargar datos
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al eliminar el usuario');
    } finally {
      setDeleting(false);
    }
  };

  

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      fullName: '',
      roles: [],
      businessAreas: [],
      isActive: true,
      password: '',
      confirmPassword: ''
    });
    setEditingUser(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowUserModal(true);
  };

  // Configurar la tabla usando el hook existente
  const table = useAdvanceTable({
    data: users,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  if (loading) {
    return (
      <Container fluid className="px-3">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4 card card-body">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-2">
                <FontAwesomeIcon icon="users" className="me-2 text-primary" />
                Gestión de Usuarios
              </h2>
              <h5 className="text-700 fw-semi-bold">
                Administra usuarios, roles y permisos del sistema
              </h5>
            </Col>
            <Col xs="auto">
              <Button 
                variant="primary" 
                onClick={openCreateModal}
              >
                <FontAwesomeIcon icon="plus" className="me-1" />
                Nuevo Usuario
              </Button>
            </Col>
          </Row>
        </div>

        {/* Estadísticas */}
        <Row className="g-3 mb-4">
          <Col lg={3} md={6}>
            <Card className="bg-primary text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{users.length}</h4>
                    <small>Total Usuarios</small>
                  </div>
                  <FontAwesomeIcon icon="users" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-success text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{users.filter(u => u.isActive).length}</h4>
                    <small>Usuarios Activos</small>
                  </div>
                  <FontAwesomeIcon icon="check-circle" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-warning text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{users.filter(u => u.roles?.some(role => role.name === 'admin')).length}</h4>
                    <small>Administradores</small>
                  </div>
                  <FontAwesomeIcon icon="user-shield" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-info text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{users.filter(u => u.isOnline).length}</h4>
                    <small>En Línea Ahora</small>
                  </div>
                  <FontAwesomeIcon icon="wifi" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Panel de Actividad Reciente */}
        <Row className="g-3 mb-4">
          <Col lg={6}>
            <Card className="shadow-sm">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <FontAwesomeIcon icon="clock" className="me-2 text-primary" />
                  Usuarios Conectados Recientemente
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="list-group list-group-flush">
                  {users
                    .filter(u => u.lastLoginAt)
                    .sort((a, b) => new Date(b.lastLoginAt) - new Date(a.lastLoginAt))
                    .slice(0, 5)
                    .map((user, index) => {
                      const lastLogin = new Date(user.lastLoginAt);
                      const now = new Date();
                      const diffInMinutes = Math.floor((now - lastLogin) / (1000 * 60));
                      
                      return (
                        <div key={user.id} className="list-group-item border-0 py-2">
                          <div className="d-flex align-items-center">
                            <Avatar
                              size="xl"
                              name={user.fullName}
                              src={user.avatar}
                              className="me-2"
                            />
                            <div className="flex-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 className="mb-0 fs-10">{user.fullName}</h6>
                                  <small className="text-muted">@{user.username}</small>
                                </div>
                                <div className="text-end">
                                  <Badge bg={user.isOnline ? 'success' : 'secondary'} className="fs-11 mb-1">
                                    {user.isOnline ? 'En línea' : 'Desconectado'}
                                  </Badge>
                                  <div>
                                    <small className="text-muted">
                                      {diffInMinutes < 60 
                                        ? `${diffInMinutes}m` 
                                        : diffInMinutes < 1440 
                                          ? `${Math.floor(diffInMinutes / 60)}h` 
                                          : `${Math.floor(diffInMinutes / 1440)}d`
                                      }
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  {users.filter(u => u.lastLoginAt).length === 0 && (
                    <div className="text-center py-3 text-muted">
                      <FontAwesomeIcon icon="clock" size="2x" className="mb-2 opacity-25" />
                      <p className="mb-0">No hay actividad reciente</p>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="shadow-sm">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <FontAwesomeIcon icon="chart-line" className="me-2 text-success" />
                  Estadísticas de Sesión
                </h6>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col xs={6}>
                    <div className="text-center">
                      <h4 className="text-success mb-1">
                        {users.filter(u => u.isOnline).length}
                      </h4>
                      <small className="text-muted">Activos Ahora</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <h4 className="text-info mb-1">
                        {users.filter(u => {
                          if (!u.lastLoginAt) return false;
                          const diffInHours = (new Date() - new Date(u.lastLoginAt)) / (1000 * 60 * 60);
                          return diffInHours < 24;
                        }).length}
                      </h4>
                      <small className="text-muted">Últimas 24h</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <h4 className="text-warning mb-1">
                        {users.filter(u => {
                          if (!u.lastLoginAt) return false;
                          const diffInDays = (new Date() - new Date(u.lastLoginAt)) / (1000 * 60 * 60 * 24);
                          return diffInDays >= 1 && diffInDays <= 7;
                        }).length}
                      </h4>
                      <small className="text-muted">Esta Semana</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <h4 className="text-danger mb-1">
                        {users.filter(u => !u.lastLoginAt || 
                          (new Date() - new Date(u.lastLoginAt)) / (1000 * 60 * 60 * 24) > 7
                        ).length}
                      </h4>
                      <small className="text-muted">Inactivos +7d</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Usuarios */}
        <Card className="shadow-sm">
          <FalconCardHeader
            title="Lista de Usuarios"
            endEl={
              <div className="d-flex gap-2">
                <Form.Control
                  type="search"
                  placeholder="Buscar usuarios..."
                  className="form-control-sm"
                  style={{ width: '200px' }}
                />
                <Button variant="falcon-default" size="sm">
                  <FontAwesomeIcon icon="filter" />
                </Button>
              </div>
            }
          />
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                headerClassName="bg-light text-900"
                bodyClassName="bg-white"
                tableProps={{
                  size: 'sm',
                  className: 'fs-10 mb-0'
                }}
              />
              <div className="px-3 pb-3">
                <AdvanceTablePagination />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>

        {/* Modal de Usuario */}
        <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={editingUser ? "edit" : "plus"} className="me-2 text-primary" />
              {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="user" className="me-1 text-primary" />
                      Nombre de Usuario *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      required
                      placeholder="Ej: juan.perez"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="user" className="me-1 text-primary" />
                      Nombre Completo *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange('fullName', e.target.value)}
                      required
                      placeholder="Ingresa el nombre completo"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="envelope" className="me-1 text-info" />
                      Email *
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      placeholder="usuario@galaxymetrics.com"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="user" className="me-1 text-warning" />
                      Roles *
                    </Form.Label>
                    <div>
                      {roles
                        .filter(role => {
                          // Solo super admins pueden ver y asignar el rol SUPER_ADMIN
                          if (role.name === 'SUPER_ADMIN') {
                            return isSuperAdmin();
                          }
                          return true;
                        })
                        .map(role => (
                        <Form.Check
                          key={role.id}
                          type="checkbox"
                          id={`role-${role.id}`}
                          label={`${role.name} - ${role.description}`}
                          checked={formData.roles.includes(role.id)}
                          onChange={(e) => {
                            const updatedRoles = e.target.checked
                              ? [...formData.roles, role.id]
                              : formData.roles.filter(r => r !== role.id);
                            handleInputChange('roles', updatedRoles);
                          }}
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="folder" className="me-1 text-success" />
                      Áreas de Negocio
                    </Form.Label>
                    <div>
                      {businessAreas.map(area => (
                        <Form.Check
                          key={area.id}
                          type="checkbox"
                          id={`area-${area.id}`}
                          label={area.name}
                          checked={formData.businessAreas.includes(area.id)}
                          onChange={(e) => {
                            const updatedAreas = e.target.checked
                              ? [...formData.businessAreas, area.id]
                              : formData.businessAreas.filter(a => a !== area.id);
                            handleInputChange('businessAreas', updatedAreas);
                          }}
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Col>
                {editingUser && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon="cog" className="me-1 text-secondary" />
                        Estado
                      </Form.Label>
                      <Form.Select
                        value={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                      >
                        {statuses.map(status => (
                          <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Los nuevos usuarios se crean como activos por defecto
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="lock" className="me-1 text-danger" />
                      {editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña *'}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      required={!editingUser}
                      placeholder="••••••••"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="lock" className="me-1 text-danger" />
                      {editingUser ? 'Confirmar Nueva Contraseña' : 'Confirmar Contraseña *'}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      required={!editingUser || formData.password}
                      placeholder="••••••••"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowUserModal(false)}
                disabled={saving}
              >
                <FontAwesomeIcon icon="times" className="me-1" />
                Cancelar
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Guardando...</span>
                    </div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={editingUser ? "check" : "plus"} className="me-1" />
                    {editingUser ? 'Actualizar Usuario' : 'Crear Usuario'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal de Confirmación de Eliminación */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2 text-warning" />
              Confirmar Eliminación
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Estás seguro de que deseas eliminar este usuario?</p>
            {userToDelete && (
              <div className="bg-light p-3 rounded">
                <strong>Usuario:</strong> {userToDelete.fullName}<br/>
                <strong>Email:</strong> {userToDelete.email}<br/>
                <strong>Roles:</strong> {userToDelete.roles?.map(role => role.name).join(', ')}
              </div>
            )}
            <p className="text-danger mt-3 mb-0">
              <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
              Esta acción no se puede deshacer.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              <FontAwesomeIcon icon="times" className="me-1" />
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Eliminando...</span>
                  </div>
                  Eliminando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="trash" className="me-1" />
                  Eliminar Usuario
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default UserManagement; 